"use client";

import { cn, Select, SelectItem } from "@kyan/ui";
import React, { useEffect } from "react";

import { marketList } from "#/config/markets";
import { useStrikes } from "#/hooks/useStrikes";
import { useIndexPrices } from "#/store/app/indexPrices/indexPrices";
import { type UnderlyingSymbol } from "#/types/markets";
import { getStrikePrecision } from "#/utils/getStrikePrecision";
import { formatValue } from "#/utils/valueFormatters";

export interface StrikeSelectorProps {
  value: number;
  maturity: string;
  className?: string;
  underlying: UnderlyingSymbol;
  onChange?: (value: number) => void;
}

export const StrikeSelector: React.FC<StrikeSelectorProps> = ({
  value,
  maturity,
  className,
  underlying,
  onChange,
}) => {
  const precision = getStrikePrecision(value);
  const market = marketList.find((m) => m.underlying === underlying);

  const { indexPrices } = useIndexPrices();
  const { strikes } = useStrikes(maturity, market);

  useEffect(() => {
    if (strikes.length && !strikes.includes(value)) {
      onChange?.(
        strikes.find((s) => s >= indexPrices[underlying]) || strikes.at(-1)!,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- We want to run this effect only when strikes or maturity change
  }, [strikes, maturity]);

  return (
    <Select
      tabIndex={-1}
      value={value.toString()}
      viewportClassName="max-h-56"
      triggerClassName={cn("h-8 text-xs", className)}
      valueComponent={formatValue(value, { precision })}
      onValueChange={(v) => {
        onChange?.(Number(v));
      }}
    >
      {strikes.map((item) => (
        <SelectItem
          showCheck
          key={item}
          className="h-8 text-xs"
          value={item.toString()}
        >
          {formatValue(item, { precision })}
        </SelectItem>
      ))}
    </Select>
  );
};
